import { useEffect, useState } from "react";
import { BsX } from "react-icons/bs";
import axiosAdmin from "../../../../../api/apiAdmin";
import { apiError } from "../../../../../api/apiError";
import { errorToast, successToast } from "../../../toastAlerts/ToastAlerts";

/**
 * Mark a parcel damaged or lost.
 *
 * The customer stops owing transport — the price goes to 0 and stays there,
 * and whatever was paid goes back (points to points, the rest to the
 * balance). The declared value of the goods, and the insurance if bought,
 * are shown; crediting that value to the balance is the admin's decision
 * (super admins only). It moves money, so the form confirms the exact
 * figures before sending, and it cannot be undone from here.
 */
const money = (n) => `${Number(n || 0).toFixed(2)} ₾`;
const field =
  "w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white text-sm outline-none focus:border-teal_600";

const IncidentModal = ({ pack, onClose, onDone }) => {
  const [info, setInfo] = useState(null);
  const [type, setType] = useState("damaged");
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [compensate, setCompensate] = useState(false);
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axiosAdmin
      .get(`/packs/${pack.id}/incident`)
      .then(({ data }) => {
        setInfo(data);
        setAmount(String(data.declared?.gel ?? ""));
      })
      .catch((err) => {
        errorToast(apiError(err, "ვერ ჩაიტვირთა"));
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.id]);

  const presets = info?.reasons?.[type] || {};
  const reason = reasonCode === "other" ? reasonText.trim() : presets[reasonCode] || "";
  const refund = info?.user ? info.pack.transport_paid : 0;
  const comp = compensate ? Number(amount || 0) : 0;

  const submit = async () => {
    setBusy(true);
    try {
      await axiosAdmin.post(`/packs/${pack.id}/incident`, {
        type,
        reason_code: reasonCode === "other" ? null : reasonCode,
        reason_text: reasonCode === "other" ? reasonText.trim() : null,
        compensate,
        compensation: compensate ? Number(amount || 0) : null,
      });
      successToast("ამანათი მოინიშნა");
      onDone && onDone();
      onClose();
    } catch (err) {
      errorToast(apiError(err, err?.response?.data?.error || "ვერ შესრულდა"));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const canGo = !!reason && (!compensate || comp > 0) && !info?.pack?.cod_pending;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-[560px] max-h-[90vh] overflow-auto bg-white dark:bg-slate-700 rounded-2xl shadow-xl p-6 sm:p-4 flex flex-col gap-4 dark:text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-bold text-lg">დაზიანდა / დაიკარგა</p>
            <p className="text-xs text-slate-500 dark:text-slate-300">
              {pack.tracking_number}
              {info?.user ? ` · #${info.user.id} ${info.user.name}` : " · მფლობელის გარეშე"}
            </p>
          </div>
          <BsX className="text-2xl text-slate-400 cursor-pointer shrink-0" onClick={onClose} />
        </div>

        {!info ? (
          <p className="py-8 text-center text-slate-400">…</p>
        ) : info.incident ? (
          <p className="text-sm">ამ ამანათზე უკვე მონიშნულია: {info.incident.reason}</p>
        ) : (
          <>
            {info.pack.cod_pending && (
              <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                ამანათი კურიერთანაა COD-ით და თანხა ჯერ არ არის აღებული — ჯერ დაასრულეთ ან გააუქმეთ OnWay შეკვეთა.
              </p>
            )}

            <div className="grid grid-cols-2 gap-2">
              {Object.entries(info.types).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setType(k);
                    setReasonCode("");
                  }}
                  className={`h-11 rounded-xl font-bold text-sm border ${
                    type === k
                      ? k === "lost"
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-orange-500 text-white border-orange-500"
                      : "border-slate-300 dark:border-slate-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-xs font-bold text-slate-500 dark:text-slate-300">მიზეზი (მომხმარებელი დაინახავს)</p>
              <select className={field} value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                <option value="">— აირჩიეთ —</option>
                {Object.entries(presets).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
                <option value="other">სხვა (ჩაწერეთ)</option>
              </select>
              {reasonCode === "other" && (
                <input
                  className={field}
                  placeholder="ჩაწერეთ მიზეზი"
                  value={reasonText}
                  maxLength={500}
                  onChange={(e) => setReasonText(e.target.value)}
                />
              )}
            </div>

            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-3 flex flex-col gap-1.5 text-sm">
              <p className="text-xs font-bold text-slate-500 dark:text-slate-300">ტრანსპორტირება</p>
              <div className="flex justify-between">
                <span>ფასი → 0, აღარ ეკისრება</span>
                <span className="font-bold">{money(info.pack.transport_price)}</span>
              </div>
              <div className="flex justify-between">
                <span>დაბრუნდება {info.refund_to === "points" ? "ქულებში" : "ბალანსზე"}</span>
                <span className="font-bold text-teal-700 dark:text-teal-300">{money(refund)}</span>
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-3 flex flex-col gap-1.5 text-sm">
              <p className="text-xs font-bold text-slate-500 dark:text-slate-300">გამოწერილი ნივთი</p>
              <div className="flex justify-between">
                <span>დეკლარირებული ღირებულება</span>
                <span className="font-bold">
                  {info.declared.amount > 0
                    ? `${Number(info.declared.amount).toFixed(2)} ${info.declared.currency || ""} = ${money(info.declared.gel)}`
                    : "არ არის დეკლარირებული"}
                </span>
              </div>
              {info.declared.items?.length > 0 && (
                <p className="text-xs text-slate-500 dark:text-slate-300">
                  {info.declared.items.map((i) => `${i.name} — ${Number(i.price).toFixed(2)}`).join(" · ")}
                </p>
              )}
              <div className="flex justify-between">
                <span>დაზღვევა</span>
                <span className={`font-bold ${info.insurance.on ? "text-teal-700 dark:text-teal-300" : "text-slate-400"}`}>
                  {info.insurance.on ? `ჩართულია (პრემია ${money(info.insurance.premium)})` : "არ იყო ჩართული"}
                </span>
              </div>

              <label className={`flex items-center gap-2 mt-2 ${info.can_compensate && info.user ? "" : "opacity-50"}`}>
                <input
                  type="checkbox"
                  checked={compensate}
                  disabled={!info.can_compensate || !info.user}
                  onChange={(e) => setCompensate(e.target.checked)}
                />
                <span className="font-bold">ღირებულება ჩაერიცხოს ბალანსზე</span>
              </label>
              {!info.can_compensate && (
                <p className="text-[11px] text-slate-500">კომპენსაციის ჩარიცხვა მხოლოდ მთავარ ადმინს შეუძლია.</p>
              )}
              {compensate && (
                <input
                  className={field}
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </div>

            {!confirming ? (
              <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose} className="rounded-lg text-sm font-bold px-4 py-2.5 text-slate-500">
                  გაუქმება
                </button>
                <button
                  type="button"
                  disabled={!canGo}
                  onClick={() => setConfirming(true)}
                  className="rounded-lg bg-red-600 disabled:opacity-40 text-white text-sm font-bold px-5 py-2.5"
                >
                  გაგრძელება
                </button>
              </div>
            ) : (
              <div className="rounded-xl border-2 border-red-300 p-3 flex flex-col gap-2 text-sm">
                <p className="font-bold">დაადასტურეთ — უკან დაბრუნება შეუძლებელია:</p>
                <ul className="list-disc pl-5 text-xs flex flex-col gap-0.5">
                  <li>
                    {info.types[type]}: {reason}
                  </li>
                  <li>ტრანსპორტირების ფასი {money(info.pack.transport_price)} → 0</li>
                  {refund > 0 && (
                    <li>
                      {money(refund)} დაუბრუნდება {info.refund_to === "points" ? "ქულებში" : "ბალანსზე"}
                    </li>
                  )}
                  {comp > 0 && <li>{money(comp)} კომპენსაცია ჩაირიცხება ბალანსზე</li>}
                  {type === "lost" && <li>OnWay-ს აღარ გაეგზავნება</li>}
                </ul>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setConfirming(false)} className="rounded-lg text-sm font-bold px-4 py-2 text-slate-500">
                    უკან
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={submit}
                    className="rounded-lg bg-red-600 disabled:opacity-50 text-white text-sm font-bold px-5 py-2"
                  >
                    {busy ? "…" : "დადასტურება"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IncidentModal;
